/**
 * Pure classification of the errors `RouteErrorBoundary` recovers from.
 *
 * Extracted from the boundary component so the matching logic is unit-testable
 * without pulling in React / posthog-js (and so this critical, high-blast-radius
 * code — 40+ route `error.tsx` files re-export the boundary — has coverage).
 *
 * All three kinds share one root cause: a session running a STALE Next.js
 * bundle after a deploy rotated the webpack chunk hashes / Server Action ids.
 * They share one recovery: reload once to pick up the new build.
 */

/**
 * The stale-bundle failure modes, distinguished only for telemetry — they all
 * recover the same way (`reloadOnceForStaleBundle`).
 */
export type StaleDeployKind =
  | "chunk"
  | "server-action-skew"
  | "element-type-invalid";

// Chunk-load failures from a stale Next.js bundle after a deploy. The
// webpack chunk hashes have rotated; sessions open across the deploy hit
// `__webpack_require__` on factory ids that no longer exist. Triggered
// most often by a `next/link` prefetch, so users see it as a random
// "page broken" until refresh. Match string covers both the explicit
// "Loading chunk N failed" path and the implicit
// `e[a].call` TypeError that surfaces when the factory map lookup
// returns undefined.
const CHUNK_LOAD_ERROR_PATTERN =
  /Loading chunk \d+ failed|undefined is not an object \(evaluating '[a-zA-Z]\[[a-zA-Z]\]\.call'\)|ChunkLoadError/i;

export function isChunkLoadError(error: Error): boolean {
  if (error.name === "ChunkLoadError") return true;
  if (typeof error.message === "string" && CHUNK_LOAD_ERROR_PATTERN.test(error.message)) {
    return true;
  }
  if (typeof error.stack === "string" && CHUNK_LOAD_ERROR_PATTERN.test(error.stack)) {
    return true;
  }
  return false;
}

// Server Action version skew is the same stale-bundle problem as a chunk
// failure, just on the data path: a tab opened before a deploy invokes a
// server action whose id was rotated in the new build. Next.js surfaces it
// either as a client-thrown `UnrecognizedActionError` (when the server
// returns the not-found header) or as the framework error text relayed in a
// text/plain 5xx ("Failed to find Server Action ... older or newer
// deployment"). We deliberately do NOT match Next's generic "unexpected
// response" error (code E394) — that also covers genuine 5xx failures and
// must not trigger an auto-reload loop that hides real backend errors.
//
// BRE-5657: `Server action not found.` is a FOURTH surface, and the one this
// deployment actually produces. Verified against staging by POSTing an
// unknown `Next-Action` id: the origin answers `404 text/plain` with exactly
// that body. It is safe to match for the same reason E394 is not - the string
// is emitted only for an unrecognized action id, so it cannot fire on a
// genuine 5xx, whereas E394 covers both and would auto-reload through real
// backend errors.
//
// Caveat worth knowing before relying on this: on the paths where Next
// collapses the response into its generic E394 message, the body text never
// reaches the client, so this pattern cannot match and skew stays
// unclassifiable. That is why `captureQueryFailed` (get-query-client.ts) was
// added alongside it - the telemetry is what makes the case visible when the
// string is unavailable.
const SERVER_ACTION_SKEW_PATTERN =
  /Failed to find Server Action|was not found on the server|older or newer deployment|Server action not found/i;

export function isServerActionVersionSkewError(error: Error): boolean {
  if (error.name === "UnrecognizedActionError") return true;
  // Thrown by deprecated/renamed server-action shims
  // (@brev-io/common/src/server-actions/deprecated). The shim's message also
  // matches SERVER_ACTION_SKEW_PATTERN below, so this name check is the fast
  // path and the message check is the fallback if the name is ever stripped.
  if (error.name === "DeprecatedActionError") return true;
  if (
    typeof error.message === "string" &&
    SERVER_ACTION_SKEW_PATTERN.test(error.message)
  ) {
    return true;
  }
  if (
    typeof error.stack === "string" &&
    SERVER_ACTION_SKEW_PATTERN.test(error.stack)
  ) {
    return true;
  }
  return false;
}

// React "element type is invalid" (#130) is the OTHER way a stale bundle
// surfaces: the chunk factory loads, but the component module's expected
// export is gone after the deploy's hashes rotated, so the import resolves to
// `undefined` and React throws #130 at render — instead of failing at the
// `X[Y].call` site that CHUNK_LOAD_ERROR_PATTERN catches. (BRE-4413: seen on
// the /agent surface after BRE-4408 + BRE-4409 rotated the agent chunks
// back-to-back; the boundary showed the generic "Something went wrong"
// dead-end because this signature wasn't recognized as a stale deploy.)
// Match only minified React's code + react.dev URL, never dev's "Element type is
// invalid" text. `pnpm local up --prod` (BRE-7960) is minified too, so
// classifyStaleDeploy skips this on stage local, where a #130 is a real bug; the
// once-per-session reload guard caps a deployed bug that produces #130.
const ELEMENT_TYPE_INVALID_PATTERN =
  /Minified React error #130\b|react\.dev\/errors\/130(?!\d)/i;

export function isElementTypeInvalidError(error: Error): boolean {
  if (
    typeof error.message === "string" &&
    ELEMENT_TYPE_INVALID_PATTERN.test(error.message)
  ) {
    return true;
  }
  if (
    typeof error.stack === "string" &&
    ELEMENT_TYPE_INVALID_PATTERN.test(error.stack)
  ) {
    return true;
  }
  return false;
}

/**
 * Classify an error as a recoverable stale-deploy symptom, or `null` if it's a
 * genuine error the boundary should surface. Order matters only for telemetry
 * labelling (the recovery is identical); chunk + server-action are checked
 * first so their long-standing event mapping is unchanged. On the `local`
 * stage no deploy rotates hashes under an open tab, so a #130 there is a bug.
 */
export function classifyStaleDeploy(
  error: Error,
  stage: string | undefined = process.env.NEXT_PUBLIC_SST_STAGE,
): StaleDeployKind | null {
  if (isChunkLoadError(error)) return "chunk";
  if (isServerActionVersionSkewError(error)) return "server-action-skew";
  if (stage !== "local" && isElementTypeInvalidError(error)) return "element-type-invalid";
  return null;
}
